import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formatPrice } from './PlanPricingTabs';
import {
  BILLING_CYCLES,
  pricingForCycle,
  applyMonthlyDefaultChange,
} from '../../utils/planPricing';

/**
 * Super admin pricing table: default price, selling price, auto discount & savings.
 */
export default function PlanPricingForm({ form, onChange }) {
  const handleMonthlyDefaultChange = (value) => {
    onChange(applyMonthlyDefaultChange({ ...form, default_price_monthly: value }, value));
  };

  const handleSellingChange = (cycle, value) => {
    onChange({ ...form, [`price_${cycle}`]: value });
  };

  return (
    <div className="space-y-3">
      <Label className="text-base font-semibold">Pricing</Label>
      <p className="text-xs text-muted-foreground">
        Set the monthly default price as the base. Half-yearly and yearly defaults are calculated automatically.
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="text-left p-3 font-medium">Cycle</th>
              <th className="text-left p-3 font-medium">Default Price</th>
              <th className="text-left p-3 font-medium">Selling Price</th>
              <th className="text-left p-3 font-medium">Discount %</th>
              <th className="text-left p-3 font-medium">Amount Saved</th>
            </tr>
          </thead>
          <tbody>
            {BILLING_CYCLES.map(({ key, label }) => {
              const pricing = pricingForCycle(form, key);
              const sellingExceedsDefault = pricing.selling_price > pricing.default_price + 0.001;
              const isMonthly = key === 'monthly';

              return (
                <tr key={key} className="border-b border-border last:border-0">
                  <td className="p-3 font-medium">{label}</td>
                  <td className="p-3">
                    {isMonthly ? (
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.default_price_monthly ?? ''}
                        onChange={(e) => handleMonthlyDefaultChange(e.target.value)}
                        className="max-w-[140px]"
                      />
                    ) : (
                      <span className="text-muted-foreground">{formatPrice(pricing.default_price)}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form[`price_${key}`] ?? ''}
                      onChange={(e) => handleSellingChange(key, e.target.value)}
                      className={`max-w-[140px] ${sellingExceedsDefault ? 'border-destructive' : ''}`}
                    />
                    {sellingExceedsDefault && (
                      <p className="text-xs text-destructive mt-1">Cannot exceed default</p>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {pricing.discount_percent > 0 ? `${pricing.discount_percent}%` : '—'}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {pricing.amount_saved > 0 ? formatPrice(pricing.amount_saved) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
